import React, { useEffect, useState } from 'react';
import { FileText, ShieldCheck, AlertTriangle, Gift, Clock, MapPin, ListOrdered } from 'lucide-react';
import API_URL from '../../config';

import SEO from './SEO';

export default function PagePravidla() {
    const [rules, setRules] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetch(`${API_URL}/rules`).then(r => r.json()).then(setRules).catch(() => {});
        fetch(`${API_URL}/routes`).then(r => r.json()).then(setRoutes).catch(() => {});
        fetch(`${API_URL}/rule-categories`).then(r => r.json()).then(setCategories).catch(() => {});
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
            <SEO 
                title="Pravidlá a Trasy | Malodunajský vodácky maratón"
                description="Kompletné pravidlá maratónu, informácie o kategóriách a podrobný popis trás na Malom Dunaji."
                keywords="pravidlá maratónu, trasa splavu, kategórie kanoe, bezpečnosť na vode"
            />
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-widest flex items-center gap-4 mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <FileText className="w-10 h-10 md:w-12 md:h-12 text-yellow-400" />
                <span className="text-white drop-shadow-md">Pravidlá a Trasy</span>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                {/* Ľavý stĺpec */}
                <div className="col-span-1 lg:col-span-2 space-y-8">

                    {/* Všeobecné pravidlá — z DB alebo statické */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
                        <h3 className="text-yellow-400 font-black text-2xl uppercase tracking-widest mb-6 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <ShieldCheck className="text-yellow-400 w-8 h-8" /> Všeobecné Pravidlá
                        </h3>
                        <div className="space-y-4">
                            {rules.length > 0 ? rules.map(rule => {
                                const styles = {
                                    danger: { box: 'bg-red-600/20 border border-red-500', icon: <AlertTriangle className="text-red-500 w-8 h-8 shrink-0 mt-1" />, title: 'text-red-400', text: 'text-white' },
                                    warning: { box: 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-blue-900', icon: <Gift className="w-8 h-8 shrink-0 mt-1" />, title: 'text-blue-900', text: 'text-blue-900/80' },
                                    info: { box: 'bg-blue-900/50 rounded-xl p-4 border border-white/10', icon: <Clock className="text-cyan-400 w-8 h-8 shrink-0" />, title: 'text-cyan-400', text: 'text-white' },
                                    standard: { box: 'bg-blue-900/40 rounded-xl p-4 border border-white/10', icon: null, title: 'text-yellow-400', text: 'text-white/90' },
                                };
                                const s = styles[rule.type] || styles.standard;
                                return (
                                    <div key={rule.id} className={`rounded-xl p-4 flex gap-4 items-start shadow-md ${s.box}`}>
                                        {s.icon}
                                        <div>
                                            <h4 className={`font-black uppercase tracking-wider mb-1 ${s.title}`}>{rule.title}</h4>
                                            <p className={`font-medium text-sm leading-relaxed ${s.text}`} dangerouslySetInnerHTML={{ __html: rule.content }} />
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="text-white/80 font-medium">Nepodarilo sa načítať pravidlá.</div>
                            )}
                        </div>
                    </div>

                    {/* Trasy */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
                        <h3 className="text-cyan-400 font-black text-2xl uppercase tracking-widest mb-6 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <MapPin className="text-cyan-400 w-8 h-8" /> Trasy Maratónu
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {routes.length > 0 ? routes.map(route => (
                                <div key={route.id} className="bg-blue-900 border border-blue-700 rounded-2xl p-5 relative overflow-hidden shadow-md">
                                    <div className="absolute top-0 right-0 bg-yellow-400 text-blue-900 font-black px-3 py-1.5 rounded-bl-xl shadow-md">{route.distance_km} km</div>
                                    <h4 className="text-yellow-400 font-black text-xl uppercase tracking-widest mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{route.name}</h4>
                                    {route.start_location && <p className="text-white font-bold mb-1">{route.start_location}</p>}
                                    {route.end_location && <p className="text-white/60 text-sm mb-3">→ {route.end_location}</p>}
                                    {route.categories && route.categories.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-auto">
                                            {route.categories.map(c => <span key={c} className="bg-white/10 text-white text-[0.65rem] px-2 py-0.5 rounded uppercase font-bold">{c}</span>)}
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <div className="text-white/80 font-medium">Nepodarilo sa načítať trasy.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Pravý stĺpec — Kategórie */}
                <div className="space-y-8">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
                        <h3 className="text-white font-black text-2xl uppercase tracking-widest mb-6 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            <ListOrdered className="text-cyan-400 w-8 h-8" /> Kategórie a Podmienky
                        </h3>
                        <div className="space-y-4">
                            {categories.sort((a,b)=>a.order-b.order).map((cat, i) => (
                                <div key={i} className="rounded-xl p-4 border border-white/10 bg-blue-900/40">
                                    <h4 className={`font-black uppercase text-lg mb-1 ${cat.color || 'text-yellow-400'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{cat.title}</h4>
                                    <p className="text-white/90 text-sm leading-relaxed mb-1">{cat.text}</p>
                                    {cat.note && <p className="text-white/70 text-xs bg-black/20 p-2 rounded mt-2">{cat.note}</p>}
                                    {cat.badge && <span className="inline-block mt-2 text-red-400 font-bold text-xs uppercase tracking-wider">{cat.badge}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
